import React from 'react';
import { makeAutoObservable } from 'mobx';
import { debounce } from 'lodash';

import { apiFetch } from 'framework/api';
import { MobxManager } from 'framework/mobx-integration';
import { FieldType, RulesConfiguration } from 'controls/queryBuilder/ruleDefinition';
import { AntSelectOption } from 'controls/react/ant/antSelect';
import { ModalPosition, openModal } from 'controls/react/ant/antModal';
import {
	DatasourceSelectionConfig,
	DatasourceSelectionConfigProps,
	SelectionMode,
} from 'controls/designer/features/widgets/datasources/datasourceSelectionConfig';
import { getDataSourceFiltersForPivotConfiguration } from 'controls/designer/features/widgets/datasources/api';
import { CategoryColumnName } from 'controls/designer/features/widgets/datasources/shared/constants';
import { i18n } from 'controls/designer/features/widgets/datasources/shared/translations';
import { PivotColumnOption, PivotConfigStore, PivotManagerConfig, PivotModel } from './types';

export class PivotManager<TModel extends PivotModel> implements PivotConfigStore<TModel> {
	columnsOptions: PivotColumnOption[] = [];
	rowsOptions: PivotColumnOption[] = [];
	legendOptions: PivotColumnOption[] = [];
	datasourceRuleConfig: RulesConfiguration;
	allColumnsRuleConfig: RulesConfiguration;
	columnsWarningMessage: string;

	rowsAreDynamic: boolean = true;
	columnsAreDynamic: boolean = true;

	requestId: string;
	private mobx = new MobxManager();
	private closeSelectorGridModal: () => void;

	get model() {
		return this.config.model;
	}

	constructor(public config: PivotManagerConfig<TModel>) {
		this.requestId = config.requestId;
		makeAutoObservable(this);

		this.mobx.reaction(
			() => [this.model.dataSourceId, this.model.accountId, this.model.pivotMatrix],
			() => this.refresh(),
			{ fireImmediately: true },
		);

		this.mobx.reaction(
			() => this.model.categoryColumns?.length,
			() => this.refreshDebounced(),
		);

		this.mobx.reaction(
			() => [this.model.dataSourceId, this.model.pivotMatrix],
			() => {
				this.rowsAreDynamic = true;
				this.columnsAreDynamic = true;
			},
		);

		this.mobx.reaction(
			() => this.rowsAreDynamic,
			() => {
				if (this.rowsAreDynamic) {
					this.model.dataRows = null;
					this.model.pivotValues = null;
				}
			},
		);

		this.mobx.reaction(
			() => this.columnsAreDynamic,
			() => {
				if (this.columnsAreDynamic) {
					this.model.dataColumns = null;
					this.model.pivotColumns = null;
				}
			},
		);

		this.mobx.reaction(
			() => [this.model.dataColumns?.length, this.model.pivotColumns?.length],
			() => {
				this.columnsAreDynamic = !this.model.dataColumns?.length && !this.model.pivotColumns?.length;
			},
			{ fireImmediately: true },
		);

		this.mobx.reaction(
			() => [this.model.dataRows?.length, this.model.pivotValues?.length],
			() => {
				this.rowsAreDynamic = !this.model.dataRows?.length && !this.model.pivotValues?.length;
			},
			{ fireImmediately: true },
		);

		this.mobx.reaction(x => this.model.categoryColumns, () => {
			if (!this.model.categoryColumns?.length) {
				this.model.dataRows = null;
			}
		});
		this.mobx.reaction(x => this.model.pivotRows, () => {
			if (!this.model.pivotRows?.length) {
				this.model.pivotValues = null;
			}
		})
	}

	refresh = async () => {
		await this.refreshRuleConfiguration();
		await this.loadColumns();
		await this.loadRows();
	};

	refreshDebounced = debounce(this.refresh, 700);

	refreshRuleConfiguration = async () => {
		if (!this.model.dataSourceId || !this.model.accountId) {
			this.datasourceRuleConfig = undefined;
			this.allColumnsRuleConfig = undefined;
			return;
		}

		const [result, allColumnsResult] = await Promise.all([
			apiFetch(
				getDataSourceFiltersForPivotConfiguration({
					requestId: this.requestId,
					accountId: this.model.accountId,
					dataSourceId: this.model.dataSourceId,
					categoryColumns: this.model.categoryColumns,
				}),
			),
			// it needs to display already selected columns because this method doesn't return columns which already selected
			apiFetch(
				getDataSourceFiltersForPivotConfiguration({
					requestId: this.requestId,
					accountId: this.model.accountId,
					dataSourceId: this.model.dataSourceId,
				}),
			),
		]);

		if (result.success) {
			this.datasourceRuleConfig = result.data;
			if (result.warning) {
				this.columnsWarningMessage = result.warning;
			}
		}
		if (allColumnsResult.success) {
			this.allColumnsRuleConfig = allColumnsResult.data;
		}
	};

	optionRenderer = (option: AntSelectOption) => {
		return (
			<>
				{option.label}
				{option.data?.type && <> ({option.data.type == FieldType.Number ? i18n('Number') : i18n('Text')})</>}
			</>
		);
	};

	loadColumns = async () => {
		if (!this.model.dataSourceId || !this.model.accountId || !this.allColumnsRuleConfig) {
			this.columnsOptions = [];
			this.legendOptions = [];
			return;
		}

		const sortedItems = Object.keys(this.allColumnsRuleConfig)
			.filter((key) => key != 'id')
			.map((x) => ({ ...this.allColumnsRuleConfig[x], key: x }))
			.sort((a, b) => a.label.localeCompare(b.label));

		this.columnsOptions = sortedItems
			.filter((item) => item.key != CategoryColumnName)
			.map((item) => ({
				label: item.label || (item.key == CategoryColumnName ? 'Default (rowHeader)' : ''),
				value: item.key,
				type: item.type,
			}));

		this.legendOptions = sortedItems
			.filter((item) => item.key != CategoryColumnName)
			.map((item) => ({
				label: item.label || (item.key == CategoryColumnName ? 'Default (rowHeader)' : ''),
				value: item.key,
				type: item.type,
			}));
	};

	loadRows = async () => {
		if (!this.model.dataSourceId || !this.model.accountId || !this.datasourceRuleConfig) {
			this.rowsOptions = [];
			return;
		}

		if (this.model.pivotMatrix) {
			this.rowsOptions = Object.keys(this.datasourceRuleConfig)
				.filter((key) => this.datasourceRuleConfig[key].type == FieldType.Number)
				.map((x) => ({
					label: this.datasourceRuleConfig[x].label,
					value: x,
					type: this.datasourceRuleConfig[x].type,
				}))
				.sort((a, b) => a.label.localeCompare(b.label));
		} else {
			this.rowsOptions =
				this.datasourceRuleConfig[CategoryColumnName]?.fieldSettings.listValues.map((item) => ({
					label: item.title,
					value: item.value,
				})) ?? [];
		}
	};

	editColumns = async () => {
		await this.editData({
			selectionMode: SelectionMode.Columns,
			value: this.model.pivotMatrix ? this.model.pivotColumns : this.model.dataColumns,
			onChange: this.onColumnsByGridUpdate,
		});
	};

	onColumnsByGridUpdate = (values: string[]) => {
		if (this.model.pivotMatrix) {
			this.model.pivotColumns = values;
		} else {
			this.model.dataColumns = values;
		}
		this.closeSelectorGridModal();
	};

	editCategoryColumn = async () => {
		await this.editData({
			selectionMode: SelectionMode.CategoryColumns,
			value: this.model.pivotMatrix ? this.model.pivotRows : this.model.categoryColumns,
			onChange: this.onCategoryColumnByGridUpdate,
		});
	};

	onCategoryColumnByGridUpdate = (values: string[]) => {
		if (this.model.pivotMatrix) {
			this.model.pivotRows = values;
		} else {
			this.model.categoryColumns = values;
		}
		this.closeSelectorGridModal();
	};

	editRows = async () => {
		if (this.model.pivotMatrix) {
			await this.editData({
				selectionMode: SelectionMode.Columns,
				value: this.model.pivotValues,
				onChange: this.onRowsByGridUpdate,
				fieldType: FieldType.Number,
				allOptions: this.rowsOptions.map((x) => x.value),
			});
		} else {
			await this.editData({
				selectionMode: SelectionMode.Rows,
				value: this.model.dataRows,
				onChange: this.onRowsByGridUpdate,
				categoryColumns: this.model.categoryColumns,
				allOptions: this.rowsOptions.map((x) => x.value),
			});
		}
	};

	onRowsByGridUpdate = (values: string[]) => {
		if (this.model.pivotMatrix) {
			this.model.pivotValues = values;
		} else {
			this.model.dataRows = values;
		}
		this.closeSelectorGridModal();
	};

	editData = async (settings: Partial<DatasourceSelectionConfigProps>) => {
		if (this.closeSelectorGridModal) {
			this.closeSelectorGridModal();
		}

		const modalResult = openModal(
			{
				title: '',
				width: 1000,
				height: 500,
				resizable: true,
				footer: null,
				mask: false,
				positionType: ModalPosition.Mouse,
			},
			<DatasourceSelectionConfig
				dataSourceId={this.model.dataSourceId}
				accountId={this.model.accountId}
				requestId={this.requestId}
				onCancel={() => this.closeSelectorGridModal()}
				{...settings}
			/>,
		);
		this.closeSelectorGridModal = modalResult.close;
	};

	closeColumnsWarning = () => {
		this.columnsWarningMessage = undefined;
	};

	destroy() {
		this.mobx.destroy();
		this.closeSelectorGridModal?.();
	}
}
